"use client";

import { useEffect, useState } from "react";
import type { Background, BackgroundPage } from "@/lib/types/background";

interface Props {
  page: BackgroundPage;
  /** ユーザーが選択中の背景ID。指定時はデフォルト背景より優先される */
  overrideId?: string;
}

export default function PageBackground({ page, overrideId }: Props) {
  const [bg, setBg] = useState<Background | null>(null);

  useEffect(() => {
    // "none" = 明示的に背景を外した状態 → 何も表示しない
    if (overrideId === "none") {
      setBg(null);
      return;
    }
    const url = overrideId
      ? `/api/backgrounds?backgroundId=${encodeURIComponent(overrideId)}`
      : `/api/backgrounds?page=${page}`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: Background | null) => setBg(data))
      .catch(() => setBg(null));
  }, [page, overrideId]);

  if (!bg?.imageUrl) return null;

  return (
    <>
      {/* 背景メディア */}
      {bg.mediaType === 'video' ? (
        <video
          key={bg.imageUrl}
          autoPlay
          loop
          muted
          playsInline
          aria-hidden="true"
          className="fixed inset-0 -z-20 h-full w-full object-cover"
        >
          <source src={bg.imageUrl} />
        </video>
      ) : (
        <div
          className="fixed inset-0 -z-20 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${bg.imageUrl})` }}
          aria-hidden="true"
        />
      )}
      {/* オーバーレイ（半透明の暗いシート） */}
      <div
        className="fixed inset-0 -z-10 bg-black"
        style={{ opacity: bg.overlayOpacity }}
        aria-hidden="true"
      />
      {/* ビネット: 上部・下部を暗くしてヘッダー・ナビの文字視認性を向上 */}
      <div
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, transparent 22%, transparent 72%, rgba(0,0,0,0.5) 100%)",
        }}
        aria-hidden="true"
      />
    </>
  );
}
